import {
  EmissionFormulaOp,
  IEmissionRuleFormula,
  IEmissionRuleInput,
  ISurveyQuestionGroupEmissionRule,
  ISurveyQuestionGroupItem,
} from '../../services/survey/survey.model';

/** Fixed alias used by simple (single question) rules: one input + top-level formula { op: alias, value }. */
export const SINGLE_QUESTION_ALIAS = 'value';

export function isLiteralInput(inp: IEmissionRuleInput): boolean {
  return inp.kind === 'literal';
}

/** A rule is "simple" when it takes exactly one question answer as its output. */
export function detectSingleQuestionMode(inputs: IEmissionRuleInput[], formula: IEmissionRuleFormula): boolean {
  if (!inputs?.length || inputs.length !== 1) return false;
  const inp = inputs[0];
  if (isLiteralInput(inp)) return false;
  const alias = typeof inp.alias === 'string' ? inp.alias.trim() : '';
  if (!alias || typeof inp.questionId !== 'string') return false;
  if (formula.op !== 'alias') return false;
  return String(formula.value ?? '') === alias;
}

export const defaultFormula = (): IEmissionRuleFormula => ({
  op: 'multiply',
  args: [
    { op: 'alias', value: '' },
    { op: 'alias', value: '' },
  ],
});

export function normalizeFormula(f: IEmissionRuleFormula): IEmissionRuleFormula {
  if (f.op === 'literal') {
    const raw = f.value as unknown;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) {
      throw new Error('Formula contains a non-numeric literal.');
    }
    return { op: 'literal', value: n };
  }
  if (f.args?.length) {
    return { ...f, args: f.args.map(normalizeFormula) };
  }
  return f;
}

export function extractAliasRefs(f: IEmissionRuleFormula | null | undefined): string[] {
  if (!f) return [];
  if (f.op === 'alias' && typeof f.value === 'string') return [f.value];
  return (f.args ?? []).flatMap(a => extractAliasRefs(a));
}

// ── Linear (step-by-step) formula model ──────────────────────────────────────

export type LinearOp = '×' | '÷' | '+' | '−';

export const LINEAR_OPS: { value: LinearOp; label: string }[] = [
  { value: '×', label: 'Multiply (×)' },
  { value: '÷', label: 'Divide (÷)' },
  { value: '+', label: 'Add (+)' },
  { value: '−', label: 'Subtract (−)' },
];

export interface LinearFormulaStep {
  op: LinearOp;
  valueKind: 'alias' | 'literal';
  alias: string;
  literal: number | undefined;
}

export interface LinearFormula {
  startKind: 'alias' | 'literal';
  startAlias: string;
  startLiteral: number | undefined;
  steps: LinearFormulaStep[];
}

export function defaultLinearFormula(): LinearFormula {
  return { startKind: 'alias', startAlias: '', startLiteral: undefined, steps: [] };
}

const linearOpToFormulaOp: Record<LinearOp, EmissionFormulaOp> = {
  '×': 'multiply', '÷': 'divide', '+': 'add', '−': 'subtract',
};
const formulaOpToLinearOp: Partial<Record<EmissionFormulaOp, LinearOp>> = {
  multiply: '×', divide: '÷', add: '+', subtract: '−',
};

export function linearToTree(lf: LinearFormula): IEmissionRuleFormula {
  let current: IEmissionRuleFormula = lf.startKind === 'alias'
    ? { op: 'alias', value: lf.startAlias || '' }
    : { op: 'literal', value: lf.startLiteral ?? 0 };
  for (const step of lf.steps) {
    const right: IEmissionRuleFormula = step.valueKind === 'alias'
      ? { op: 'alias', value: step.alias || '' }
      : { op: 'literal', value: step.literal ?? 0 };
    current = { op: linearOpToFormulaOp[step.op], args: [current, right] };
  }
  return current;
}

/** Null when the tree is not a simple left-leaning spine of binary ops (an "advanced" formula). */
export function treeToLinear(tree: IEmissionRuleFormula): LinearFormula | null {
  function flattenSpine(
    node: IEmissionRuleFormula,
  ): Array<{ value: IEmissionRuleFormula; op?: LinearOp }> | null {
    if (node.op === 'alias' || node.op === 'literal') return [{ value: node }];
    const lin = formulaOpToLinearOp[node.op];
    if (!lin || !node.args || node.args.length !== 2) return null;
    const right = node.args[1];
    if (right.op !== 'alias' && right.op !== 'literal') return null;
    const left = flattenSpine(node.args[0]);
    if (!left) return null;
    return [...left, { value: right, op: lin }];
  }
  const flat = flattenSpine(tree);
  if (!flat || flat.length === 0) return null;
  const first = flat[0].value;
  return {
    startKind: first.op === 'alias' ? 'alias' : 'literal',
    startAlias: first.op === 'alias' ? String(first.value ?? '') : '',
    startLiteral: first.op === 'literal' ? (Number.isFinite(Number(first.value)) ? Number(first.value) : 0) : undefined,
    steps: flat.slice(1).map(({ value, op }) => ({
      op: op!,
      valueKind: value.op === 'alias' ? 'alias' : 'literal',
      alias: value.op === 'alias' ? String(value.value ?? '') : '',
      literal: value.op === 'literal' ? (Number.isFinite(Number(value.value)) ? Number(value.value) : 0) : undefined,
    })),
  };
}

// ── Preview evaluation ───────────────────────────────────────────────────────

export function formatNum(n: number | null | undefined): string {
  if (n == null) return '?';
  if (Number.isInteger(n)) return String(n);
  return n.toLocaleString(undefined, { maximumSignificantDigits: 4 });
}

export function traceFormula(
  f: IEmissionRuleFormula,
  bindings: Record<string, number | null>,
): Array<{ label: string; result: number | null }> {
  const steps: Array<{ label: string; result: number | null }> = [];
  const OP_SYMBOLS: Partial<Record<EmissionFormulaOp, string>> = {
    multiply: '×', divide: '÷', add: '+', subtract: '−',
  };

  function evalNode(node: IEmissionRuleFormula): number | null {
    if (node.op === 'literal') {
      const n = typeof node.value === 'number' ? node.value : Number(node.value);
      return Number.isFinite(n) ? n : null;
    }
    if (node.op === 'alias') {
      const v = typeof node.value === 'string' ? bindings[node.value] : null;
      return v != null && Number.isFinite(v) ? v : null;
    }
    const childResults = (node.args ?? []).map(a => evalNode(a));
    let result: number | null = null;
    switch (node.op) {
      case 'multiply': result = childResults[0] != null && childResults[1] != null ? childResults[0] * childResults[1] : null; break;
      case 'divide': result = childResults[0] != null && childResults[1] != null && childResults[1] !== 0 ? childResults[0] / childResults[1] : null; break;
      case 'add': result = childResults[0] != null && childResults[1] != null ? childResults[0] + childResults[1] : null; break;
      case 'subtract': result = childResults[0] != null && childResults[1] != null ? childResults[0] - childResults[1] : null; break;
      case 'sum': { const v = childResults.filter((a): a is number => a != null); result = v.length > 0 ? v.reduce((s, x) => s + x, 0) : null; break; }
      case 'coalesce': result = childResults.find(a => a != null) ?? null; break;
    }
    const argLabels = (node.args ?? []).map((a, i) => {
      if (a.op === 'alias') return String(a.value ?? '');
      return formatNum(childResults[i]);
    });
    const sym = OP_SYMBOLS[node.op];
    const label = sym && argLabels.length === 2
      ? `${argLabels[0]} ${sym} ${argLabels[1]}`
      : `${node.op}(${argLabels.join(', ')})`;
    steps.push({ label, result });
    return result;
  }

  evalNode(f);
  return steps;
}

// ── Plain-language rendering ─────────────────────────────────────────────────

/**
 * Renders any formula tree as a readable expression, e.g. "(distance × 2.68) + offset".
 * Used for the read-only view of advanced formulas the step editor cannot show,
 * and for rule summaries in the calculations list.
 */
export function formulaToText(f: IEmissionRuleFormula | null | undefined): string {
  if (!f) return '';
  const OP_SYMBOLS: Partial<Record<EmissionFormulaOp, string>> = {
    multiply: '×', divide: '÷', add: '+', subtract: '−',
  };
  function render(node: IEmissionRuleFormula, parenthesise: boolean): string {
    if (node.op === 'literal') return String(node.value ?? '0');
    if (node.op === 'alias') return String(node.value ?? '');
    const sym = OP_SYMBOLS[node.op];
    if (sym && node.args?.length === 2) {
      const text = `${render(node.args[0], true)} ${sym} ${render(node.args[1], true)}`;
      return parenthesise ? `(${text})` : text;
    }
    return `${node.op}(${(node.args ?? []).map(a => render(a, false)).join(', ')})`;
  }
  return render(f, false);
}

/** One-line plain-language summary of a rule for list rows. */
export function summarizeRule(
  rule: ISurveyQuestionGroupEmissionRule,
  groupItems: ISurveyQuestionGroupItem[],
): string {
  const promptFor = (questionId: string | undefined): string | null => {
    if (!questionId) return null;
    const prompt = groupItems.find(i => i.surveyQuestionId === questionId)?.surveyQuestion?.prompt;
    return prompt ? `"${prompt}"` : null;
  };

  let valuePart: string;
  if (detectSingleQuestionMode(rule.inputs ?? [], rule.formula)) {
    const q = promptFor(rule.inputs[0]?.questionId);
    valuePart = q ? `Answer to ${q}` : 'One answer value';
  } else {
    const aliasQuestion = new Map(
      (rule.inputs ?? [])
        .filter(i => i.kind !== 'literal' && i.kind !== 'date' && i.kind !== 'adjustmentRef' && i.questionId)
        .map(i => [i.alias, promptFor(i.questionId)]),
    );
    const text = formulaToText(rule.formula);
    // A formula over one question alias reads better with the prompt inlined.
    valuePart = aliasQuestion.size === 1 && text
      ? `${text} (${[...aliasQuestion.values()][0] ?? 'answer'})`
      : text || 'Formula';
  }

  if (rule.isAdjustment && rule.outputAlias) {
    return `Produces "${rule.outputAlias}" = ${valuePart}`;
  }

  const factorPart = rule.conversionId ? ' × emission factor' : '';
  const conditions = rule.scopeMetadata?.applyWhen?.allOf ?? [];
  let conditionPart = '';
  if (conditions.length > 0) {
    const firstQ = promptFor(conditions[0].questionId);
    conditionPart = firstQ ? `, when ${firstQ} matches` : ', with conditions';
    if (conditions.length > 1) conditionPart += ` (+${conditions.length - 1} more)`;
  }
  return `${valuePart}${factorPart}${conditionPart}`;
}
